/**
 * Composite phone input: searchable country-code dropdown + local number.
 * The parent always receives/stores the combined E.164-ish value
 * ("+<dialcode><national digits>") via `composePhoneValue`.
 */
import React from 'react';
import CountryCodeSelect from './CountryCodeSelect';
import { getCountryByIso2, DEFAULT_NSN_MIN, DEFAULT_NSN_MAX } from '../../constants/countryCodes';

export const composePhoneValue = (iso2, national) => {
  const digits = (national || '').replace(/\D/g, '');
  if (!digits) return '';
  const country = getCountryByIso2(iso2);
  return `${country.dialCode}${digits}`;
};

/** Client-side mirror of the backend's per-country length check (UX only — the
 * backend re-validates with the app's own generic min/max + mandatory '+'). */
export const validatePhoneNational = (iso2, national, appName = 'This field') => {
  const digits = (national || '').replace(/\D/g, '');
  if (!digits) return null;
  const country = getCountryByIso2(iso2);
  const min = country.nsnMin || DEFAULT_NSN_MIN;
  const max = country.nsnMax || DEFAULT_NSN_MAX;
  if (digits.length < min || digits.length > max) {
    return `Enter a valid ${country.name} number for ${appName} (${min}${min !== max ? `–${max}` : ''} digits after ${country.dialCode}).`;
  }
  return null;
};

const PhoneNumberField = ({ iso2, national, onChangeIso2, onChangeNational, error, testId }) => {
  const country = getCountryByIso2(iso2);
  const placeholder = country.example ? `${country.dialCode} ${country.example}` : `${country.dialCode} phone number`;

  return (
    <div className="flex gap-2">
      <CountryCodeSelect value={iso2} onChange={onChangeIso2} testId={`${testId}-country`} />
      <input
        type="tel"
        inputMode="numeric"
        value={national}
        onChange={(e) => onChangeNational(e.target.value.replace(/\D/g, ''))}
        placeholder={placeholder}
        data-testid={testId}
        aria-invalid={!!error}
        className={`flex-1 w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-white ${
          error ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:ring-slate-900'
        }`}
      />
    </div>
  );
};

export default PhoneNumberField;
